import { useCallback, useEffect, useMemo, useState } from "react"
import { debounce } from "lodash"

import useUrlQueryParams from "hooks/useUrlQueryParams"

import { TableSearchContent } from "./TableSearchContent"

interface Props {
  name?: string
  label?: string
  type: "text" | "number"
}

export const TableSearch = ({ name, label, type }: Props) => {
  const [value, setValue] = useState("")
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()

  const handleClear = () => {
    setValue("")
    if (name) {
      setUrlQueryParams({ [name]: null })
    }
  }

  const onInputChange = useCallback(
    (inputValue: string) => {
      if (name) {
        setUrlQueryParams({
          [name]: type === "number" ? inputValue : inputValue.trim(),
        })
      }
    },
    [name, type, setUrlQueryParams],
  )

  const debouncedOnInputChange = useMemo(() => debounce(onInputChange, 500), [onInputChange])

  useEffect(() => () => debouncedOnInputChange.cancel(), [debouncedOnInputChange])

  const handleInputChange = useCallback(
    (inputValue: string) => {
      const filteredValue = type === "number" ? inputValue.replace(/\D/g, "") : inputValue
      setValue(filteredValue)
      debouncedOnInputChange(filteredValue)
    },
    [type, debouncedOnInputChange],
  )

  useEffect(() => {
    if (name && urlQueryParams[name] && !value) {
      setValue(urlQueryParams[name])
    } else if (urlQueryParams && name && !Object.hasOwn(urlQueryParams, name)) {
      setValue("")
    }
  }, [urlQueryParams])

  return (
    <TableSearchContent
      name={name}
      label={label}
      value={value}
      onClear={handleClear}
      onInputChange={handleInputChange}
    />
  )
}
